import { Box, Button, CircularProgress, Typography } from '@mui/material';

import { BORDER_RADIUS, semanticColors } from '@/theme/tokens';
import SaveStatus, { type SaveState } from './SaveStatus';

interface SectionSaveRowProps {
  /** Shown only while this group differs from what's stored. */
  dirty: boolean;
  state: SaveState;
  error?: string | null;
  /** False while the edit is incomplete — e.g. a half-typed phone number. */
  canSave: boolean;
  /** Names the thing being saved, e.g. "Save name". */
  saveLabel: string;
  cancelLabel: string;
  savingLabel: string;
  savedLabel: string;
  /** Explains why Save is inert, shown in place of the status chip. */
  blockedHint?: string | null;
  onSave: () => void;
  onCancel: () => void;
}

/**
 * The commit affordance for a group of text fields, rendered inside that
 * group's card. Living next to the inputs keeps it in the same scroll position
 * as the field being edited — a bar pinned to the bottom of the viewport ends
 * up behind the on-screen keyboard on mobile web, which is what this replaces.
 */
export const SectionSaveRow = ({
  dirty,
  state,
  error,
  canSave,
  saveLabel,
  cancelLabel,
  savingLabel,
  savedLabel,
  blockedHint,
  onSave,
  onCancel,
}: SectionSaveRowProps) => {
  const saving = state === 'saving';

  // Once clean, the row steps aside but a just-finished "Saved" still gets its
  // moment — otherwise the confirmation unmounts the instant the save lands.
  if (!dirty) {
    if (state === 'saved' || state === 'error') {
      return (
        <Box sx={{ mt: 1.75 }}>
          <SaveStatus
            state={state}
            error={error}
            savingLabel={savingLabel}
            savedLabel={savedLabel}
          />
        </Box>
      );
    }
    return null;
  }

  const showHint = state === 'idle' && !!blockedHint && !canSave;

  return (
    // Stacked, not side-by-side: at phone widths a hint like "Pick an address
    // from the suggestions" would otherwise wrap to four lines squeezed beside
    // the buttons. Its own line reads cleanly at every width.
    <Box sx={{ mt: 2, pt: 1.75, borderTop: `1px solid ${semanticColors.borderLight}` }}>
      {(showHint || state !== 'idle') && (
        <Box sx={{ mb: 1 }}>
          {showHint ? (
            <Typography sx={{ fontSize: '12px', color: semanticColors.textTertiary }}>
              {blockedHint}
            </Typography>
          ) : (
            <SaveStatus
              state={state}
              error={error}
              savingLabel={savingLabel}
              savedLabel={savedLabel}
            />
          )}
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
        <Button
          size="small"
          onClick={onCancel}
          disabled={saving}
          variant="text"
          sx={{
            textTransform: 'none',
            fontSize: '13px',
            borderRadius: BORDER_RADIUS,
            color: semanticColors.textSecondary,
            '&:hover': { background: semanticColors.overlaySubtle },
          }}
        >
          {cancelLabel}
        </Button>
        <Button
          size="small"
          onClick={onSave}
          disabled={saving || !canSave}
          variant="contained"
          startIcon={
            saving ? (
              <CircularProgress size={12} sx={{ color: semanticColors.textOnPrimary }} />
            ) : undefined
          }
          sx={{
            textTransform: 'none',
            fontSize: '13px',
            // The spinner widens the button mid-save; without this the label
            // breaks onto a second line and the row jumps.
            whiteSpace: 'nowrap',
            borderRadius: BORDER_RADIUS,
            backgroundColor: semanticColors.textPrimary,
            boxShadow: 'none',
            '&:hover': { backgroundColor: semanticColors.brandAccent, boxShadow: 'none' },
            '&:active': { boxShadow: 'none' },
            '&.Mui-disabled': { backgroundColor: semanticColors.disabledBackground },
          }}
        >
          {saveLabel}
        </Button>
      </Box>
    </Box>
  );
};

export default SectionSaveRow;
